import { useEffect, useMemo, useState } from "react";
import type { CraftablesIndex, CraftableWeaponSummary } from "@/lib/crafting/types";
import {
  fetchCraftedWeapons,
  type CraftedWeaponInstance,
  type LoadResult,
  type SessionInfo,
} from "@/lib/manifest/client";
import { deleteSavedPlan, loadSavedPlans, type SavedPlan } from "@/lib/crafting/plan";
import { assetUrl } from "@/lib/bungie/assets";
import { WeaponCatalogue } from "./WeaponCatalogue";
import { matchesSearch } from "@/lib/manifest/catalogue";

export type CatalogueTab = "patterns" | "owned" | "plans";

export interface CatalogueScreenProps {
  tab: CatalogueTab;
  catalogue: CraftablesIndex;
  session: SessionInfo;
  onTabChange: (tab: CatalogueTab) => void;
  onBack: () => void;
  onOpenWeapon: (recipeHash: number) => void;
  onOpenCrafted: (instance: CraftedWeaponInstance, recipeHash: number) => void;
}

/**
 * The catalogue, in three sections: every craftable pattern, the weapons you
 * already own, and your saved plans.
 *
 * Owned weapons get their own section rather than living on the hub because an
 * established account can have dozens, which needs the same search the pattern
 * list has.
 */
export function CatalogueScreen({
  tab,
  catalogue,
  session,
  onTabChange,
  onBack,
  onOpenWeapon,
  onOpenCrafted,
}: CatalogueScreenProps) {
  const [crafted, setCrafted] = useState<LoadResult<CraftedWeaponInstance[]> | null>(null);
  const [plans, setPlans] = useState<SavedPlan[]>(() => loadSavedPlans(window.localStorage));

  // Fetched once here and shared by the tab counts and the owned list.
  useEffect(() => {
    if (!session.signedIn) {
      setCrafted(null);
      return;
    }
    let cancelled = false;
    void fetchCraftedWeapons().then((r) => {
      if (!cancelled) setCrafted(r);
    });
    return () => {
      cancelled = true;
    };
  }, [session.signedIn]);

  const ownedCount = crafted?.status === "ok" ? crafted.data.length : undefined;

  return (
    <div>
      <div className="mx-auto max-w-[110rem] px-4 pt-5 sm:px-8">
        <button
          type="button"
          onClick={onBack}
          className="label-caps mb-3 text-xs text-ink-2 transition-colors hover:text-ink"
        >
          ← Back
        </button>

        <nav aria-label="Catalogue sections" className="flex flex-wrap gap-1">
          <Tab active={tab === "patterns"} onClick={() => onTabChange("patterns")}>
            Patterns <Count value={catalogue.weaponCount} />
          </Tab>
          <Tab active={tab === "owned"} onClick={() => onTabChange("owned")}>
            My Weapons {ownedCount !== undefined && <Count value={ownedCount} />}
          </Tab>
          <Tab active={tab === "plans"} onClick={() => onTabChange("plans")}>
            My Plans <Count value={plans.length} />
          </Tab>
        </nav>
      </div>

      {tab === "patterns" && (
        <WeaponCatalogue
          weapons={catalogue.weapons}
          manifestVersion={catalogue.manifestVersion}
          onOpen={onOpenWeapon}
        />
      )}

      {tab === "owned" && (
        <OwnedWeapons
          catalogue={catalogue}
          session={session}
          result={crafted}
          onOpen={onOpenCrafted}
        />
      )}

      {tab === "plans" && (
        <SavedPlans
          catalogue={catalogue}
          plans={plans}
          crafted={crafted?.status === "ok" ? crafted.data : undefined}
          onOpen={onOpenWeapon}
          onOpenCrafted={onOpenCrafted}
          onDelete={(id) => {
            deleteSavedPlan(window.localStorage, id);
            setPlans(loadSavedPlans(window.localStorage));
          }}
        />
      )}
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={[
        "label-caps border px-3 py-2 text-xs transition-colors",
        active ? "border-line bg-white/16 text-ink" : "border-line-dim text-ink-2 hover:bg-white/8",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Count({ value }: { value: number }) {
  return <span className="ml-1.5 tracking-normal text-ink-3">{value}</span>;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-[110rem] px-4 py-5 sm:px-8">{children}</div>;
}

function OwnedWeapons({
  catalogue,
  session,
  result,
  onOpen,
}: {
  catalogue: CraftablesIndex;
  session: SessionInfo;
  result: LoadResult<CraftedWeaponInstance[]> | null;
  onOpen: (instance: CraftedWeaponInstance, recipeHash: number) => void;
}) {
  const [search, setSearch] = useState("");
  const byItemHash = useMemo(
    () => new Map(catalogue.weapons.map((w) => [w.itemHash, w])),
    [catalogue.weapons]
  );

  if (!session.signedIn) {
    return (
      <Shell>
        <p className="text-sm text-ink-3">
          Sign in with Bungie to see the weapons you have already shaped, with their real levels and
          equipped perks.
        </p>
        <a
          href="/auth/bungie/start"
          className="type-button mt-4 inline-block border border-line bg-white/10 px-4 py-2 text-xs transition-colors hover:bg-white/20"
        >
          Sign in with Bungie
        </a>
      </Shell>
    );
  }

  if (result === null) {
    return (
      <Shell>
        <p className="text-sm text-ink-3">Loading your weapons…</p>
      </Shell>
    );
  }
  if (result.status !== "ok") {
    return (
      <Shell>
        <p className="text-sm text-level">{result.message}</p>
      </Shell>
    );
  }

  const rows = result.data
    .map((instance) => ({ instance, summary: byItemHash.get(instance.itemHash) }))
    .filter((r): r is { instance: CraftedWeaponInstance; summary: CraftableWeaponSummary } =>
      Boolean(r.summary)
    )
    .filter((r) => matchesSearch(r.summary, search))
    // Highest level first: the ones closest to unlocking everything.
    .sort((a, b) => (b.instance.weaponLevel ?? 0) - (a.instance.weaponLevel ?? 0));

  return (
    <Shell>
      <label htmlFor="owned-search" className="sr-only">
        Search your weapons
      </label>
      <input
        id="owned-search"
        type="search"
        value={search}
        placeholder="Search your weapons…"
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 w-full max-w-sm border border-line-dim bg-black/50 px-3 py-2 text-sm focus:border-line"
      />

      {rows.length === 0 ? (
        <p className="text-sm text-ink-3">
          {result.data.length === 0
            ? "No crafted weapons found on this account."
            : "No crafted weapons match that search."}
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-px bg-line-dim/40 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {rows.map(({ instance, summary }) => (
            <li key={instance.itemInstanceId}>
              <button
                type="button"
                onClick={() => onOpen(instance, summary.recipeHash)}
                className="flex w-full items-center gap-2.5 bg-black/45 px-2.5 py-2 text-left transition-colors hover:bg-white/10"
              >
                {assetUrl(summary.icon) ? (
                  <img
                    src={assetUrl(summary.icon)}
                    alt=""
                    aria-hidden
                    className="h-10 w-10 shrink-0"
                  />
                ) : (
                  <span aria-hidden className="h-10 w-10 shrink-0 bg-white/10" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="label-caps block truncate text-xs font-semibold">
                    {summary.name}
                  </span>
                  <span className="block truncate text-[0.65rem] text-ink-3">
                    {summary.itemTypeDisplayName}
                    {instance.location === "vault" ? " · Vault" : " · Character"}
                  </span>
                </span>
                {instance.weaponLevel !== undefined && (
                  <span className="label-caps shrink-0 text-[0.65rem] text-ink-2">
                    Lv. {instance.weaponLevel}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}

function SavedPlans({
  catalogue,
  plans,
  crafted,
  onOpen,
  onOpenCrafted,
  onDelete,
}: {
  catalogue: CraftablesIndex;
  plans: SavedPlan[];
  crafted?: CraftedWeaponInstance[];
  onOpen: (recipeHash: number) => void;
  onOpenCrafted: (instance: CraftedWeaponInstance, recipeHash: number) => void;
  onDelete: (id: string) => void;
}) {
  const byRecipeHash = useMemo(
    () => new Map(catalogue.weapons.map((w) => [w.recipeHash, w])),
    [catalogue.weapons]
  );
  const byInstanceId = useMemo(
    () => new Map((crafted ?? []).map((c) => [c.itemInstanceId, c])),
    [crafted]
  );

  if (plans.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-ink-3">
          No saved plans yet. Build one and choose “Save Reshape Plan” on the review screen — plans
          are stored in this browser.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <ul className="divide-y divide-line-dim/60 border border-line-dim">
        {plans.map((plan) => {
          const weapon = byRecipeHash.get(plan.w);
          // A plan built against a weapon you own follows that weapon's level.
          const instance = plan.instanceId ? byInstanceId.get(plan.instanceId) : undefined;
          return (
            <li key={plan.id} className="flex items-center gap-3 bg-black/40 px-3 py-2">
              <button
                type="button"
                onClick={() =>
                  instance ? onOpenCrafted(instance, plan.w) : onOpen(plan.w)
                }
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                {assetUrl(weapon?.icon) ? (
                  <img
                    src={assetUrl(weapon?.icon)}
                    alt=""
                    aria-hidden
                    className="h-9 w-9 shrink-0"
                  />
                ) : (
                  <span aria-hidden className="h-9 w-9 shrink-0 bg-white/10" />
                )}
                <span className="min-w-0">
                  <span className="label-caps block truncate text-sm">{plan.name}</span>
                  <span className="block truncate text-xs text-ink-3">
                    {weapon?.name ?? `Weapon ${plan.w}`} ·{" "}
                    {new Date(plan.savedAt).toLocaleDateString()}
                    {plan.instanceId &&
                      (instance
                        ? instance.weaponLevel !== undefined
                          ? ` · now Lv. ${instance.weaponLevel}`
                          : " · tracking your weapon"
                        : crafted
                          ? " · weapon no longer on your account"
                          : " · sign in to track progress")}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onDelete(plan.id)}
                aria-label={`Delete ${plan.name}`}
                className="label-caps shrink-0 border border-line-dim px-2 py-1 text-[0.6rem] text-ink-3 transition-colors hover:bg-white/10 hover:text-ink"
              >
                Delete
              </button>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}
